<template>
	<section class="section-base form-section-base">
		<div class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<div class="form-section-base__title">Сведения о предыдущих должниках</div>
				<div class="form-section-base__sub-title">
					(заполняется, если должник, указанный в документе, на основании которого
					совершается исполнительная надпись, отличается от должника, указанного в
					шаге 4 настоящего заявления)
				</div>
				<div class="form-section-base__controls">
					<v-row>
						<v-col>
							<v-checkbox
								v-model="isDebtors"
								:disabled="isSurcharge"
								label="Должник, указанный в документе, на основании которого совершается исполнительная надпись, отличается от должника, указанного в шаге 4 настоящего заявления"
								hide-details
							/>
						</v-col>
					</v-row>
				</div>

				<template v-if="isDebtors">
					<Debtor
						v-for="(v, index) in $v.debtors.$each.$iter"
						:v="v"
						:key="index"
						:can-delete="debtors.length > 1 && !isSurcharge"
						:disabled="isSurcharge"
						@delete="deleteDebtor(index)"
						@change-reason="changeReasonHandler({ index, value: $event })"
					/>

					<v-row>
						<v-col v-if="!isSurcharge" cols="12">
							<v-btn
								depressed
								color="primary"
								class="button-base"
								@click="addDebtor"
							>
								<v-icon size="20" left>{{ mdiAccountMultiplePlus }}</v-icon>
								Добавить должника
							</v-btn>
						</v-col>

					</v-row>
				</template>
			</div>
		</div>

		<div class="form-section-base__block-outer" v-if="isDebtors">
			<div class="form-section-base__block-inner">
				<v-col cols="12" class="mt-8">
					<div class="form-base__title">
						Документы, подтверждающие изменение должника
					</div>
				</v-col>
				<v-col cols="12">
					<AttachDocumentMultiple
						v-model="$v.documents.$model"
						:errorMessages="getValidationErrorText($v.documents)"
						:disabled="isSurcharge"
            :withSelect="statementType === StatementType.NOTICE_RECOVERY && isNoticeRecoveryClassifierEnable"
						:document-types="documentTypes"
						translationKey="noticeRecovery.debtors.attachMultiple"
					/>
				</v-col>
			</div>
		</div>
		<v-container>
			<Footer
				@next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
				@prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
				:loading-next="loadingNext"
			/>
		</v-container>
	</section>
</template>

<script>
import {requiredIf} from 'vuelidate/lib/validators';
import {mdiAccountMultiplePlus} from '@mdi/js';
import cloneDeep from 'clone-deep';

import {checkInvalid, leaveStep} from '@/utils/services';
import {DOCS_REQUIREMENTS, getValidationErrorText} from '@/utils/validate';
import {Role} from '@/utils/dictionaries';
import {ServicesModule} from '@/store/modules/services';
import AttachDocumentMultiple from '@/components/base/AttachDocument/AttachDocumentMultiple';
import Footer from '@/components/services/elements/Footer';
import {StepDirection, StepName} from '@/types/services';
import Debtor, {debtorModel, getDebtorValidation} from './Debtor';
import {NoticeRecoveryStep, StatementType} from '@/constants';
import {UploadDocumentTypes} from "@/utils/uploadDocumentTypes";
import { isNoticeRecoveryClassifierEnable } from '@/app.config';

export default {
	name: 'Debtors',
	props: {
		loadingNext: {
			type: Boolean,
			required: true,
		},
		isSurcharge: {
			type: Boolean,
			required: true,
		},
		statementType: {
			type: String,
			required: true,
		},
	},
	components: {
		Debtor,
		AttachDocumentMultiple,
		Footer,
	},
	data: () => ({
		StepDirection,
		StepName,
		leaveStep,
		getValidationErrorText,
		Role,
		mdiAccountMultiplePlus,
		role: 1,
		invalidFields: [],
		debtors: [cloneDeep(debtorModel)],
		isDebtors: false,
		documents: [],
		documentsRequired: false,
		changeReasonValues: new Set(),
		debtor: {},
		unsubscribe: null,
		StatementType,
    isNoticeRecoveryClassifierEnable,
	}),
	computed: {
		documentTypes() {
			const docsCodes = ['12299']
			return UploadDocumentTypes
				.filter((item) => ['12200'].includes(item.groupCode))
				.map((item) => {
					return {
						...item,
						documents: item.documents.filter((doc => docsCodes.includes(doc.value)))
					}
				})
		}
	},
	validations() {
		return {
			debtors: {
				$each: getDebtorValidation(this.debtor),
			},
			documents: {
				DOCUMENTS_MIN_LENGTH: requiredIf(() => this.documentsRequired),
				DOCS_REQUIREMENTS: DOCS_REQUIREMENTS(this.documents)
			},
		};
	},
	created() {
		this.prefillDraft();

		this.unsubscribe = this.$store.subscribeAction((action) => {
			if (action.type === 'saveStatement') this.fillDebtor();
		});
	},
	beforeDestroy() {
		this.unsubscribe();
	},
	watch: {
		documentsRequired(val, oldVal) {
			if (val !== oldVal) this.$v.documents.$reset();
		},
	},
	methods: {
		fillDebtor() {
			this.debtor = cloneDeep(ServicesModule.statement.subjects.debtor);
		},

		changeReasonHandler({index, value}) {
			if (value) {
				this.changeReasonValues.add(index);
			} else {
				this.changeReasonValues.delete(index);
			}

			this.documentsRequired = Boolean(this.changeReasonValues.size);
		},

		touchFields() {
			this.invalidFields = [];

			if (!this.isDebtors) return;

			this.$v.documents.$touch();
			checkInvalid(this.$v.documents, this.invalidFields);

			for (const debtor of Object.values(this.$v.debtors.$each.$iter)) {
				debtor[debtor.role.$model].$touch();
				checkInvalid(debtor[debtor.role.$model], this.invalidFields);
			}
		},

		addDebtor() {
			this.debtors.push(cloneDeep(debtorModel));
		},

		deleteDebtor(deleteIndex) {
			this.debtors = this.debtors.filter(
				(_, index) => index !== Number(deleteIndex)
			);
		},

		assemblyModel() {
			const model = {
				previousDebtors: [],
				prevDebtorDocuments: [],
			};

			if (!this.isDebtors) return model;

			const previousDebtors = this.debtors.map((item) => {
				if (item.role === Role.PERSON) {
					const itemModel = cloneDeep(item[Role.PERSON]);

					// если не является ИП, удаляем лишнии поля
					const businessmanArr = ['ogrn'];
					if (!item[Role.PERSON].isBusinessman) {
						businessmanArr.forEach((key) => {
							delete itemModel[key];
						});
					}

					return {
						[Role.PERSON]: {
							...itemModel,
						},
						changeReason: item[Role.PERSON].changeReason,
					};
				}

				if (item.role === Role.ORGANIZATION) {
					const itemModel = cloneDeep(item[Role.ORGANIZATION]);
					return {
						[Role.ORGANIZATION]: {
							...itemModel,
						},
						changeReason: item[Role.ORGANIZATION].changeReason,
					};
				}

				if (item.role === Role.FOREIGN_ORGANIZATION) {
					const itemModel = cloneDeep(item[Role.FOREIGN_ORGANIZATION]);
					return {
						[Role.FOREIGN_ORGANIZATION]: {
							...itemModel,
						},
						changeReason: item[Role.FOREIGN_ORGANIZATION].changeReason,
					};
				}
			});

			const prevDebtorDocuments = cloneDeep(this.documents);

			return {
				previousDebtors,
				prevDebtorDocuments,
			};
		},

		prefillDraft() {
			if (
				!ServicesModule.statement.subjects.previousDebtors ||
				ServicesModule.statement.subjects.previousDebtors.length === 0
			) {
				return;
			}

			this.isDebtors = true;

			const previousDebtorsStore = cloneDeep(
				ServicesModule.statement.subjects.previousDebtors
			);

			this.debtors = previousDebtorsStore.map((item, itemIndex) => {
				let itemRole = null;
				if (item[Role.PERSON]) itemRole = Role.PERSON;
				if (item[Role.ORGANIZATION]) itemRole = Role.ORGANIZATION;
				if (item[Role.FOREIGN_ORGANIZATION])
					itemRole = Role.FOREIGN_ORGANIZATION;

				let itemDefaults = [];
				if (itemRole === Role.PERSON)
					itemDefaults = [Role.ORGANIZATION, Role.FOREIGN_ORGANIZATION];
				if (itemRole === Role.ORGANIZATION)
					itemDefaults = [Role.PERSON, Role.FOREIGN_ORGANIZATION];
				if (itemRole === Role.FOREIGN_ORGANIZATION)
					itemDefaults = [Role.PERSON, Role.ORGANIZATION];

				this.changeReasonHandler({
					index: itemIndex,
					value: item.changeReason,
				});

				return {
					role: itemRole,
					[itemRole]: {
						...cloneDeep(debtorModel[itemRole]),
						...cloneDeep(item[itemRole]),
						changeReason: item.changeReason,
					},
					[itemDefaults[0]]: cloneDeep(debtorModel[itemDefaults[0]]),
					[itemDefaults[1]]: cloneDeep(debtorModel[itemDefaults[1]]),
				};
			});

			if (ServicesModule.statement.prevDebtorDocuments.length === 0) {
				return;
			}

			this.documents = cloneDeep(ServicesModule.statement.prevDebtorDocuments);
		},

		btnSaveClickHandler(direction) {
			leaveStep({
				stepThis: this,
				direction,
				step: NoticeRecoveryStep.DEBTORS,
			});
		},
	},
};
</script>

<style scoped></style>
